/**
 * Маскот · Ф2.2 → ⭐ Ф2.5 (все шесть героев) — перенос из design/mascots-v2.html.
 *
 * Вид = КОД: геометрия и цвета живут здесь, в БД лежит только ключ героя
 * (`user_progress.character`, слой 3). Перерисовка формы ничего не ломает и не требует
 * миграций (решение 25.07).
 *
 * ⭐ ЗАКОН РОСТА (из принятого прототипа, один на всех): растёт ВЕСЬ персонаж — общий
 * налив от уровня 1 к 12 (`scale`) плюс своя черта у каждого: у листовых
 * (Лингви · Морковка · Свёкла) гуще ботва, у Помидорки распускается звезда-чашелистик и
 * мягко созревает цвет, у Черешни ярче блик и на пол-пути появляется вторая ягодка,
 * у Кукурузы раскрываются листья-обёртки и наливаются зёрна.
 * На МАКСИМАЛЬНОМ уровне (12) всем достаются солнечные очки — единый слой аксессуаров.
 *
 * ⛔ Уровень витрина не считает: он приходит из ядра (`gamify.level_for_xp` по журналу
 * событий). Здесь только рисование.
 */

import "./styles/mascot.css";

export const MAX_LEVEL = 12;

/** Ключи героев — те же, что в ядре (`gamify.CHARACTERS`); сверяется check-xp.mjs. */
export type CharacterKey = "lingvi" | "carrot" | "beet" | "cherry" | "tomato" | "corn";

export const CHARACTERS: { key: CharacterKey; name: string; hint: string }[] = [
  { key: "lingvi", name: "Лингви", hint: "росток · по умолчанию" },
  { key: "carrot", name: "Морковка", hint: "оранжевый корешок" },
  { key: "beet", name: "Свёкла", hint: "фиолетовая" },
  { key: "cherry", name: "Черешня", hint: "красная всегда" },
  { key: "tomato", name: "Помидорка", hint: "созревает с уровнем" },
  { key: "corn", name: "Кукуруза", hint: "жёлтый початок" },
];

export const DEFAULT_CHARACTER: CharacterKey = "lingvi";

export function characterName(key: string): string {
  return CHARACTERS.find((c) => c.key === key)?.name ?? CHARACTERS[0].name;
}

let gid = 0;

/* ── общие части (лицо, блик, ботва, очки) ─────────────────────────────────── */

function face(cx: number, ey: number, gap: number, r: number): string {
  const lx = cx - gap, rx = cx + gap;
  return (
    '<g class="eyes">' +
    `<ellipse class="eye" cx="${lx}" cy="${ey}" rx="${r}" ry="${(r * 1.15).toFixed(1)}"/>` +
    `<ellipse class="eye" cx="${rx}" cy="${ey}" rx="${r}" ry="${(r * 1.15).toFixed(1)}"/>` +
    `<circle class="pupil" cx="${lx + 1}" cy="${ey + 2}" r="${(r * 0.46).toFixed(1)}"/>` +
    `<circle class="pupil" cx="${rx + 1}" cy="${ey + 2}" r="${(r * 0.46).toFixed(1)}"/>` +
    `<circle class="shine" cx="${lx - 1.4}" cy="${ey - 1.2}" r="1.5"/>` +
    `<circle class="shine" cx="${rx - 1.4}" cy="${ey - 1.2}" r="1.5"/></g>` +
    `<circle class="cheek" cx="${cx - gap - 5}" cy="${ey + 12}" r="4.6"/>` +
    `<circle class="cheek" cx="${cx + gap + 5}" cy="${ey + 12}" r="4.6"/>` +
    `<path class="smile" d="M${cx - 7} ${ey + 15} Q${cx} ${ey + 22} ${cx + 7} ${ey + 15}"/>`
  );
}

/** Солнечные очки — только на 12-м уровне (единый слой аксессуаров, как в прототипе). */
function shades(cx: number, ey: number, gap: number, r: number): string {
  const w = Math.min(r * 2.3, gap * 1.7), h = r * 2.0, rad = (r * 0.8).toFixed(1);
  const lx = cx - gap, rxc = cx + gap, by = ey - h * 0.1;
  const lens = (ex: number) => {
    const x = ex - w / 2, y = ey - h / 2;
    return (
      `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" ` +
      `height="${h.toFixed(1)}" rx="${rad}" fill="#191b12"/>` +
      `<rect x="${(x + w * 0.16).toFixed(1)}" y="${(y + h * 0.16).toFixed(1)}" ` +
      `width="${(w * 0.32).toFixed(1)}" height="${(h * 0.22).toFixed(1)}" ` +
      `rx="${(r * 0.35).toFixed(1)}" fill="#fff" opacity=".24"/>`
    );
  };
  let out = '<g class="glasses">';
  out += `<path class="shades-arm" stroke-width="${(r * 0.5).toFixed(1)}" ` +
         `d="M${(lx - w / 2).toFixed(1)} ${by.toFixed(1)} ` +
         `L${(lx - w / 2 - r * 1.3).toFixed(1)} ${(by - r * 0.7).toFixed(1)}"/>`;
  out += `<path class="shades-arm" stroke-width="${(r * 0.5).toFixed(1)}" ` +
         `d="M${(rxc + w / 2).toFixed(1)} ${by.toFixed(1)} ` +
         `L${(rxc + w / 2 + r * 1.3).toFixed(1)} ${(by - r * 0.7).toFixed(1)}"/>`;
  if ((rxc - w / 2) - (lx + w / 2) > 0.5) {
    out += `<rect x="${(lx + w / 2).toFixed(1)}" y="${by.toFixed(1)}" ` +
           `width="${((rxc - w / 2) - (lx + w / 2)).toFixed(1)}" ` +
           `height="${(r * 0.4).toFixed(1)}" rx="${(r * 0.2).toFixed(1)}" fill="#191b12"/>`;
  }
  return out + lens(lx) + lens(rxc) + "</g>";
}

function gloss(cx: number, cy: number, g: number, sp = 1): string {
  return (
    `<ellipse cx="${(cx - 7 * sp).toFixed(1)}" cy="${(cy - 10 * sp).toFixed(1)}" ` +
    `rx="${((5 + 3 * g) * sp).toFixed(1)}" ry="${((3.4 + 2 * g) * sp).toFixed(1)}" ` +
    `fill="#fff" opacity="${(0.14 + 0.22 * g).toFixed(2)}"/>`
  );
}

/** Ботва: листья из ОДНОЙ точки вверх, с уровнем веер раскрывается (закон роста). */
function tuft(bx: number, by: number, level: number, scale: number,
              nMin: number, nMax: number, spMin: number, spMax: number): string {
  const t = (level - 1) / (MAX_LEVEL - 1);
  const n = Math.max(1, Math.round(nMin + (nMax - nMin) * t));
  const spread = spMin + (spMax - spMin) * t;
  let out = "";
  for (let i = 0; i < n; i++) {
    const k = n === 1 ? 0.5 : i / (n - 1);
    const ang = -spread + k * spread * 2;
    const mid = 1 - Math.abs(k - 0.5) * 1.05;
    const s = (0.70 + 0.52 * mid) * scale;
    out += `<path class="${i % 2 ? "leaf" : "leaf2"}" transform="translate(${bx},${by}) ` +
           `rotate(${ang.toFixed(1)}) scale(${s.toFixed(2)})" ` +
           'd="M0 0 C -6 -8 -6 -20 0 -27 C 6 -20 6 -8 0 0 Z"/>';
  }
  return out;
}

function leafSVG(x: number, y: number, rot: number, s = 1): string {
  return `<path class="leaf" transform="translate(${x},${y}) rotate(${rot}) scale(${s})" ` +
         'd="M0 0 C -6 -7 -6 -18 0 -24 C 6 -18 6 -7 0 0 Z"/>';
}

/* созревание цвета (Помидорка) — те же остановки, что в прототипе */
function h2r(h: string): number[] {
  const s = h.replace("#", "");
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
function r2h(a: number[]): string {
  return "#" + a.map((x) => {
    const s = Math.max(0, Math.min(255, Math.round(x))).toString(16);
    return s.length < 2 ? "0" + s : s;
  }).join("");
}
function ripen(stops: string[], t: number): string {
  const n = stops.length - 1;
  const seg = Math.min(n - 1, Math.floor(t * n));
  const lt = t * n - seg, a = h2r(stops[seg]), b = h2r(stops[seg + 1]);
  return r2h([a[0] + (b[0] - a[0]) * lt, a[1] + (b[1] - a[1]) * lt, a[2] + (b[2] - a[2]) * lt]);
}
function dk(hex: string, f: number): string {
  const c = h2r(hex);
  return r2h([c[0] * f, c[1] * f, c[2] * f]);
}

/* ── герои ─────────────────────────────────────────────────────────────────── */

type Draw = (level: number, g: number, max: boolean, gradId: string) => string;

const lingvi: Draw = (level, g, max, gradId) => {
  let s = tuft(60, 36, level, 1.05, 4, 16, 16, 88);
  s += `<path fill="url(#${gradId})" d="M60 36 C85 36 96 55 96 76 C96 97 80 107 60 107 ` +
       'C40 107 24 97 24 76 C24 55 35 36 60 36 Z"/>';
  s += '<ellipse cx="60" cy="88" rx="24" ry="19" fill="#fff" opacity=".14"/>' + gloss(52, 58, g, 1.1);
  s += face(60, 72, 13, 10);
  return max ? s + shades(60, 72, 13, 10) : s;
};

const carrot: Draw = (level, g, max) => {
  let s = tuft(60, 44, level, 1.12, 3, 9, 14, 66);
  s += '<path fill="var(--carrot)" d="M60 44 C74 44 82 50 82 56 C82 68 70 104 60 110 ' +
       'C50 104 38 68 38 56 C38 50 46 44 60 44 Z"/>';
  s += '<path stroke="var(--carrot-d)" stroke-width="2.4" stroke-linecap="round" opacity=".5" ' +
       'fill="none" d="M50 62 q10 3 20 0 M52 76 q8 3 16 0 M54 90 q6 2 12 0"/>' + gloss(53, 58, g, 0.9);
  s += face(60, 64, 11, 8.5);
  return max ? s + shades(60, 64, 11, 8.5) : s;
};

const beet: Draw = (level, g, max) => {
  let s = tuft(60, 48, level, 1.0, 3, 10, 14, 70);
  s += '<path fill="var(--beet)" d="M60 48 C80 48 90 63 88 80 C86 95 73 103 60 114 ' +
       'C47 103 34 95 32 80 C30 63 40 48 60 48 Z"/>';
  s += '<path class="stemp" style="stroke:var(--beet-d)" d="M60 113 C60 119 58 123 55 127"/>';
  s += '<ellipse cx="60" cy="88" rx="17" ry="15" fill="#fff" opacity=".12"/>' + gloss(52, 64, g, 1);
  s += face(60, 76, 12, 9);
  return max ? s + shades(60, 76, 12, 9) : s;
};

const cherry: Draw = (_level, g, max) => {
  const cx = 60, cy = 80, r = 20;
  let s = `<path class="stemp" d="M${cx} ${cy - r + 2} C57 52 58 44 62 38"/>` + leafSVG(62, 38, 30, 0.9);
  if (g > 0.55) {                       // вторая ягодка приходит на пол-пути
    s += `<path class="stemp" d="M${cx + 6} ${cy - r + 4} C70 54 66 44 62 38"/>`;
    s += `<circle cx="${cx + 16}" cy="${cy - 2}" r="${(r * 0.82).toFixed(1)}" fill="var(--cherry-d)"/>`;
  }
  s += `<circle cx="${cx}" cy="${cy}" r="${r.toFixed(1)}" fill="var(--cherry)"/>`;
  s += `<ellipse cx="${cx}" cy="${(cy + r * 0.4).toFixed(1)}" rx="${(r * 0.78).toFixed(1)}" ` +
       `ry="${(r * 0.55).toFixed(1)}" fill="var(--cherry-d)" opacity=".28"/>` + gloss(cx, cy, g, 1);
  s += face(cx, cy - 1, 10, 8);
  return max ? s + shades(cx, cy - 1, 10, 8) : s;
};

function calyxStar(cx: number, topY: number, g: number): string {
  const n = 5, maxAng = 42 + 52 * g;
  let out = `<path class="stemp" d="M${cx} ${topY} C${cx} ${topY - 5} ${cx} ${topY - 9} ${cx} ${topY - 12}"/>`;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1), ang = -maxAng + t * maxAng * 2;
    const mid = 1 - Math.abs(t - 0.5) * 0.5, sc = 0.8 + 0.4 * mid;
    out += `<path class="${i % 2 ? "leaf" : "leaf2"}" transform="translate(${cx},${topY}) ` +
           `rotate(${ang.toFixed(1)}) scale(${sc.toFixed(2)})" ` +
           'd="M0 0 C -4.6 -6 -4.6 -15 0 -20 C 4.6 -15 4.6 -6 0 0 Z"/>';
  }
  return out;
}

const tomato: Draw = (_level, g, max) => {
  const col = ripen(["#8ab84f", "#e79a3a", "#e2543f"], g), sh = dk(col, 0.8);
  const cx = 60, cy = 74, rx = 32, ry = 27;
  let s = `<path fill="${col}" d="M${cx} ${(cy - ry + 2).toFixed(1)} ` +
    `C ${(cx + rx).toFixed(1)} ${(cy - ry + 2).toFixed(1)} ${(cx + rx + 2).toFixed(1)} ` +
    `${(cy + ry * 0.2).toFixed(1)} ${(cx + rx * 0.86).toFixed(1)} ${(cy + ry * 0.7).toFixed(1)} ` +
    `C ${(cx + rx * 0.5).toFixed(1)} ${(cy + ry).toFixed(1)} ${(cx - rx * 0.5).toFixed(1)} ` +
    `${(cy + ry).toFixed(1)} ${(cx - rx * 0.86).toFixed(1)} ${(cy + ry * 0.7).toFixed(1)} ` +
    `C ${(cx - rx - 2).toFixed(1)} ${(cy + ry * 0.2).toFixed(1)} ${(cx - rx).toFixed(1)} ` +
    `${(cy - ry + 2).toFixed(1)} ${cx} ${(cy - ry + 2).toFixed(1)} Z"/>`;
  s += `<ellipse cx="${cx}" cy="${(cy - ry + 4).toFixed(1)}" rx="${(rx * 0.42).toFixed(1)}" ` +
       `ry="${(4 + 2 * g).toFixed(1)}" fill="${sh}" opacity=".28"/>`;
  s += `<ellipse cx="${cx}" cy="${(cy + ry * 0.45).toFixed(1)}" rx="${(rx * 0.7).toFixed(1)}" ` +
       `ry="${(ry * 0.5).toFixed(1)}" fill="${sh}" opacity=".15"/>` + gloss(cx - 2, cy - 2, g, 1);
  s += calyxStar(cx, cy - ry + 5, g) + face(cx, cy + 2, 12, 9);
  return max ? s + shades(cx, cy + 2, 12, 9) : s;
};

const corn: Draw = (_level, g, max) => {
  const cx = 60;
  let s = "";
  const nl = Math.round(2 + 2 * g);                 // листья-обёртки раскрываются
  for (let i = 0; i < nl; i++) {
    const t = nl < 2 ? 0.5 : i / (nl - 1), a = -58 + t * 116, sc = 1.0 + 0.2 * g;
    s += `<path class="${i % 2 ? "leaf" : "leaf2"}" transform="translate(${cx},104) ` +
         `rotate(${a.toFixed(0)}) scale(${sc.toFixed(2)})" ` +
         'd="M0 6 C -11 -8 -11 -30 0 -40 C 11 -30 11 -8 0 6 Z"/>';
  }
  s += '<path fill="var(--corn)" d="M60 42 C74 42 82 52 82 66 C82 92 72 108 60 108 ' +
       'C48 108 38 92 38 66 C38 52 46 42 60 42 Z"/>';
  s += '<g opacity=".55">';
  for (let r = 0; r < 4; r++) {
    const y = 76 + r * 8, off = r % 2 ? 4 : 0;
    for (let c = 0; c < 4; c++) {
      const x = cx - 14 + off + c * 8;
      if (x < cx - 15 || x > cx + 15) continue;
      s += `<ellipse cx="${x}" cy="${y}" rx="3.1" ry="3.6" fill="var(--corn-lt)"/>`;
    }
  }
  s += "</g>";
  s += '<path class="stemp" style="stroke:var(--corn-silk)" d="M56 44 C54 34 57 30 59 26 ' +
       'M60 44 C60 34 62 30 63 26 M64 45 C66 36 69 32 71 28"/>';
  s += gloss(52, 56, g, 0.9) + face(60, 60, 11, 8.5);
  return max ? s + shades(60, 60, 11, 8.5) : s;
};

const DRAW: Record<CharacterKey, Draw> = { lingvi, carrot, beet, cherry, tomato, corn };

/**
 * Герой `key` на уровне `level` (1…12). Уровень приходит из ядра; здесь он только
 * зажимается в границы, чтобы кривая XP могла меняться без правок рисования.
 */
export function drawMascot(level = 1, anim = true, key: string = DEFAULT_CHARACTER): string {
  const lv = Math.min(MAX_LEVEL, Math.max(1, Math.round(level || 1)));
  const g = (lv - 1) / (MAX_LEVEL - 1);
  const gs = (0.70 + 0.34 * g).toFixed(3);
  const id = `gAcc${++gid}`;
  const draw = DRAW[(key as CharacterKey)] ?? lingvi;
  return (
    `<svg viewBox="0 0 120 120" class="${anim ? "bob" : ""}" aria-hidden="true">` +
    `<defs><linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">` +
    '<stop offset="0" stop-color="var(--accent-2)"/><stop offset="1" stop-color="var(--accent)"/>' +
    "</linearGradient></defs>" +
    `<g transform="translate(60,112) scale(${gs}) translate(-60,-112)">` +
    `${draw(lv, g, lv >= MAX_LEVEL, id)}</g></svg>`
  );
}

/** Имя героя по умолчанию — оставлено ради совместимости со старым импортом. */
export const MASCOT_NAME = "Лингви";
